import React from "react";

import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
} from "@react-pdf/renderer";


import logo from "./savationChristLogo.png";

import studentAvatar from "./goldavartar.png";

/* ============================================================================
   REPORT CARD PDF
============================================================================ */

/* ============================================================================
   PERIOD CONSTANTS
============================================================================ */

const sem1Periods = [
  "Period 1",
  "Period 2",
  "Period 3",
  "Exam",
];

const sem2Periods = [
  "Period 4",
  "Period 5",
  "Period 6",
  "Exam2",
];

/* ============================================================================
   SUBJECT ORDERING
============================================================================ */

const SUBJECT_ORDER = {
  ECE: [
    "Health Science",
    "History",
    "English Writing",
    "English Reciting",
    "Mathematic Reciting",
    "Mathematic Writing",
    "Reading",
    "Social Studies",
    "Phonics",
    "Bible/RME",
    "Spelling",
    "Arts/Drawing",
    "General Science",
  ],

  Elementary: [
    "Math",
    "English",
    "Reading",
    "Writing",
    "Phonics",
    "Spelling",
    "General Science",
    "Social Studies",
    "Bible/RME",
    "Physical Education",
    "Arts/Drawing",
    "Health Science",
  ],
};

/* ============================================================================
   COLOR SYSTEM
============================================================================ */

const COLORS = {
  black: "#111111",
  darkBlack: "#050505",
  white: "#FFFFFF",

  deepOrange: "#D94F04",
  orange: "#F97316",
  lightOrange: "#FFF1E8",

  gray: "#5B6470",
  darkGray: "#374151",
  mediumGray: "#9CA3AF",
  lightGray: "#F3F4F6",
  borderGray: "#D1D5DB",

  tinBlue: "#EAF4FA",
  lightBlue: "#F4FAFD",
  blueBorder: "#B9D8E8",
  blueText: "#1E526B",

  chameleon: "#76A83B",
  lightChameleon: "#EEF7E7",
  darkChameleon: "#456C1F",

  red: "#C62828",
  lightRed: "#FDECEC",

  yellow: "#F4C542",
  lightYellow: "#FFF8D9",

  purple: "#665191",
  lightPurple: "#F3F0FA",
};

/* ============================================================================
   SCORE COLOR HELPER
============================================================================ */

const getColor = (value) => {
  if (
    value === "N/A" ||
    value === undefined ||
    value === null ||
    value === ""
  ) {
    return COLORS.mediumGray;
  }

  // Handle letter grades
  const textValue = String(value).trim().toUpperCase();

  if (textValue === "F") {
    return COLORS.red;
  }

  // Any other letter grade (A, B, C, D, E, etc.)
  if (
    ["A", "B", "C", "D", "E"].includes(textValue)
  ) {
    return COLORS.blueText;
  }

  // Handle numeric scores
  const numericValue = Number(value);

  if (!Number.isNaN(numericValue)) {
    // 69 and below -> RED
    if (numericValue < 70) {
      return COLORS.red;
    }

    // 70 and above -> BLUE
    return COLORS.blueText;
  }

  return COLORS.blueText;
};

/* ============================================================================
   SCORE BACKGROUND HELPER
============================================================================ */

const getScoreBackground = (value) => {
  if (
    value === "N/A" ||
    value === undefined ||
    value === null ||
    value === ""
  ) {
    return COLORS.lightGray;
  }

  const numericValue = Number(value);

  if (!Number.isNaN(numericValue) && numericValue < 70) {
    return COLORS.lightRed;
  }

  if (!Number.isNaN(numericValue) && numericValue >= 90) {
    return COLORS.lightChameleon;
  }

  return COLORS.lightBlue;
};

/* ============================================================================
   VALUE FORMATTER
============================================================================ */

const formatValue = (value) => {
  if (
    value === undefined ||
    value === null ||
    value === ""
  ) {
    return "N/A";
  }

  return value;
};

/* ============================================================================
   STYLES
============================================================================ */

const styles = StyleSheet.create({

  /* ========================================================================
     PAGE
  ======================================================================== */

  page: {
    width: "100%",
    height: "100%",
    padding: 18,
    backgroundColor: COLORS.white,
    fontFamily: "Helvetica",
    fontSize: 8,
    color: COLORS.black,
  },

  backPage: {
    width: "100%",
    height: "100%",
    padding: 18,
    backgroundColor: COLORS.white,
    fontFamily: "Helvetica",
    fontSize: 8,
    color: COLORS.black,
  },

  /* ========================================================================
     TOP ACCENT BAR
  ======================================================================== */

  topAccentBar: {
    height: 5,
    width: "100%",
    backgroundColor: COLORS.deepOrange,
    marginBottom: 6,
  },

  /* ========================================================================
     FRONT HEADER
  ======================================================================== */

  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: COLORS.tinBlue,
    borderWidth: 1,
    borderColor: COLORS.blueBorder,
    borderRadius: 7,
    padding: 8,
    minHeight: 68,
  },

  logoSection: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },

  logo: {
    width: 58,
    height: 50,
    objectFit: "contain",
    marginRight: 8,
  },

  logotwo: {
    width: 62,
    height: 48,
    objectFit: "contain",
    marginLeft: 8,
  },

  schoolIdentity: {
    flex: 1,
  },

  schoolName: {
    fontSize: 14,
    fontWeight: "bold",
    color: COLORS.black,
    marginBottom: 2,
  },

  schoolSubtitle: {
    fontSize: 8,
    color: COLORS.blueText,
    marginBottom: 2,
  },

  schoolLocation: {
    fontSize: 7,
    color: COLORS.gray,
  },

  reportCardTitle: {
    fontSize: 15,
    fontWeight: "bold",
    color: COLORS.deepOrange,
    textAlign: "right",
    marginBottom: 3,
  },

  reportCardSubtitle: {
    fontSize: 7,
    color: COLORS.gray,
    textAlign: "right",
  },

  /* ========================================================================
     STUDENT INFORMATION BAR
  ======================================================================== */

  studentInfoBar: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: COLORS.lightGray,
    borderLeftWidth: 4,
    borderLeftColor: COLORS.deepOrange,
    paddingVertical: 6,
    paddingHorizontal: 8,
    marginTop: 6,
    marginBottom: 6,
  },

  studentInfoItem: {
    flex: 1,
    paddingRight: 5,
  },

  studentInfoLabel: {
    fontSize: 6,
    color: COLORS.gray,
    textTransform: "uppercase",
    marginBottom: 1,
  },

  studentInfoValue: {
    fontSize: 8,
    fontWeight: "bold",
    color: COLORS.black,
  },

  /* ========================================================================
     SECTION HEADER
  ======================================================================== */

  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: COLORS.tinBlue,
    borderWidth: 1,
    borderColor: COLORS.blueBorder,
    paddingVertical: 5,
    paddingHorizontal: 7,
    marginTop: 5,
    marginBottom: 4,
    borderRadius: 4,
  },

  sectionTitle: {
    fontSize: 9,
    fontWeight: "bold",
    color: COLORS.blueText,
  },

  sectionTag: {
    fontSize: 6,
    color: COLORS.deepOrange,
    fontWeight: "bold",
    textTransform: "uppercase",
  },

  /* ========================================================================
     EQUAL TWO-SIDE ACADEMIC TABLE
  ======================================================================== */

  academicTableWrapper: {
    flexDirection: "row",
    width: "100%",
    borderWidth: 1,
    borderColor: COLORS.borderGray,
    borderRadius: 4,
    overflow: "hidden",
  },

  academicHalfTable: {
    width: "50%",
  },

  academicHalfTableRight: {
    width: "50%",
    borderLeftWidth: 5,
    borderLeftColor: COLORS.white,
  },

  academicHeaderRow: {
    flexDirection: "row",
    minHeight: 25,
    backgroundColor: COLORS.tinBlue,
  },

  academicBodyRow: {
    flexDirection: "row",
    minHeight: 19,
  },

  academicSubjectCell: {
    width: "50%",
    minHeight: 19,
    borderRightWidth: 0.5,
    borderBottomWidth: 0.5,
    borderColor: COLORS.borderGray,
    justifyContent: "center",
    paddingHorizontal: 5,
    fontSize: 7,
    fontWeight: "bold",
    color: COLORS.darkGray,
  },

  academicSubjectHeaderCell: {
    width: "50%",
    minHeight: 25,
    borderRightWidth: 0.5,
    borderBottomWidth: 0.5,
    borderColor: COLORS.blueBorder,
    justifyContent: "center",
    paddingHorizontal: 5,
    fontSize: 7,
    fontWeight: "bold",
    color: COLORS.blueText,
  },



  academicScoreCell: {
  width: "12.5%",
  minHeight: 19,
  borderRightWidth: 0.5,
  borderBottomWidth: 0.5,
  borderColor: COLORS.borderGray,
  justifyContent: "center",
  alignItems: "center",
  textAlign: "center",
  paddingHorizontal: 2,
  paddingVertical: 2,
  fontSize: 8.5,
  fontWeight: "bold",
},

  academicScoreHeaderCell: {
    width: "12.5%",
    minHeight: 25,
    borderRightWidth: 0.5,
    borderBottomWidth: 0.5,
    borderColor: COLORS.blueBorder,
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    paddingHorizontal: 2,
    paddingVertical: 3,
    fontSize: 7.5,
    fontWeight: "bold",
    color: COLORS.blueText,
  },

  semesterAverageCell: {
    backgroundColor: COLORS.lightBlue,
  },

  yearlyAverageCell: {
    backgroundColor: COLORS.lightYellow,
  },

  /* ========================================================================
     PERFORMANCE SUMMARY
  ======================================================================== */

  // performanceSummary: {
  //   flexDirection: "row",
  //   marginTop: 5,
  //   width: "100%",
  // },

  performanceSummary: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    marginTop: 8,
    width: "78%",
    alignSelf: "center",
},

  // summaryCard: {
  //   flex: 1,
  //   backgroundColor: COLORS.lightGray,
  //   borderWidth: 1,
  //   borderColor: COLORS.borderGray,
  //   borderRadius: 5,
  //   paddingVertical: 5,
  //   marginRight: 4,
  //   alignItems: "center",
  //   justifyContent: "center",
  // },

  summaryCard: {
    flex: 1,
    marginHorizontal: 6,
    backgroundColor: COLORS.lightGray,
    borderWidth: 1,
    borderColor: COLORS.borderGray,
    borderRadius: 6,
    paddingVertical: 8,
    alignItems: "center",
    justifyContent: "center",
},

  summaryCardLast: {
    flex: 1,
    marginHorizontal: 6,
    backgroundColor: COLORS.lightChameleon,
    borderWidth: 1,
    borderColor: COLORS.chameleon,
    borderRadius: 5,
    paddingVertical: 5,
    alignItems: "center",
    justifyContent: "center",
  },

  summaryLabel: {
    fontSize: 7.5,
    color: COLORS.gray,
    marginBottom: 2,
  },

  summaryValue: {
    fontSize: 16,
    fontWeight: "bold",
  },

  /* ========================================================================
     PROMOTION AND REMARKS
  ======================================================================== */

  promotionBox: {
    backgroundColor: COLORS.lightOrange,
    borderLeftWidth: 4,
    borderLeftColor: COLORS.deepOrange,
    padding: 6,
    marginTop: 5,
    borderRadius: 4,
  },

  promotionTitle: {
    fontSize: 7,
    fontWeight: "bold",
    color: COLORS.deepOrange,
    marginBottom: 2,
  },

  promotionText: {
    fontSize: 6.5,
    color: COLORS.darkGray,
    lineHeight: 1.25,
  },

  remarkBox: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: COLORS.black,
    padding: 6,
    marginTop: 4,
    borderRadius: 4,
  },

  remarkLabel: {
    fontSize: 7,
    fontWeight: "bold",
    color: COLORS.white,
    marginRight: 5,
  },

  remarkText: {
    flex: 1,
    fontSize: 7,
    color: COLORS.white,
  },

  /* ========================================================================
     LEGEND
  ======================================================================== */

  gradeLegend: {
    backgroundColor: COLORS.tinBlue,
    borderWidth: 1,
    borderColor: COLORS.blueBorder,
    padding: 5,
    marginTop: 4,
    textAlign: "center",
    fontSize: 6.5,
    color: COLORS.darkGray,
    borderRadius: 4,
  },

  /* ========================================================================
     CONDUCT AND RANK
  ======================================================================== */

  conductRankBar: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 5,
    paddingVertical: 4,
  },

  conductText: {
    fontSize: 7.5,
    fontWeight: "bold",
    color: COLORS.darkGray,
  },

  rankBadge: {
    backgroundColor: COLORS.black,
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },

  rankText: {
    color: COLORS.white,
    fontSize: 7.5,
    fontWeight: "bold",
  },

  /* ========================================================================
     SIGNATURES
  ======================================================================== */

  signatureSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    backgroundColor: COLORS.tinBlue,
    borderWidth: 1,
    borderColor: COLORS.blueBorder,
    borderRadius: 5,
    paddingVertical: 5,
    paddingHorizontal: 22,
    marginTop: 4,
  },

  signatureItem: {
    width: "35%",
    alignItems: "center",
  },

  signatureLabel: {
    fontSize: 6.5,
    color: COLORS.gray,
    marginBottom: 13,
  },

  signatureLine: {
    width: "100%",
    borderTopWidth: 1,
    borderTopColor: COLORS.black,
    marginBottom: 2,
  },

  signatureDate: {
    fontSize: 5.5,
    color: COLORS.gray,
  },

  /* ========================================================================
     BACK PAGE LAYOUT
  ======================================================================== */

  backPageHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: COLORS.tinBlue,
    borderWidth: 1,
    borderColor: COLORS.blueBorder,
    borderRadius: 7,
    padding: 8,
    marginBottom: 8,
  },

  backPageHeaderLeft: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },

  backPageLogo: {
    width: 50,
    height: 45,
    objectFit: "contain",
    marginRight: 8,
  },

  backPageTitle: {
    fontSize: 13,
    fontWeight: "bold",
    color: COLORS.black,
  },

  backPageSubtitle: {
    fontSize: 7,
    color: COLORS.gray,
    marginTop: 2,
  },

  backPageTag: {
    backgroundColor: COLORS.deepOrange,
    color: COLORS.white,
    fontSize: 7,
    fontWeight: "bold",
    paddingHorizontal: 8,
    paddingVertical: 5,
    borderRadius: 4,
  },

  backColumns: {
    flexDirection: "row",
    width: "100%",
    flex: 1,
  },

  backColumnLeft: {
    width: "49%",
    marginRight: "2%",
  },

  backColumnRight: {
    width: "49%",
  },

  /* ========================================================================
     BACK CARDS
  ======================================================================== */

  card: {
    backgroundColor: COLORS.white,
    borderWidth: 1,
    borderColor: COLORS.borderGray,
    borderRadius: 6,
    padding: 8,
    marginBottom: 7,
  },

  cardHeader: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: COLORS.tinBlue,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.blueBorder,
    marginHorizontal: -8,
    marginTop: -8,
    marginBottom: 7,
    paddingHorizontal: 8,
    paddingVertical: 5,
  },

  cardHeaderAccent: {
    width: 4,
    height: 14,
    backgroundColor: COLORS.deepOrange,
    marginRight: 5,
    borderRadius: 2,
  },

  cardTitle: {
    fontSize: 8,
    fontWeight: "bold",
    color: COLORS.blueText,
  },

  /* ========================================================================
     SCHOOL PROFILE
  ======================================================================== */

  schoolProfileContent: {
    alignItems: "center",
    justifyContent: "center",
  },

  schoolProfileLogo: {
    width: 52,
    height: 52,
    objectFit: "contain",
    marginBottom: 5,
  },

  schoolProfileName: {
    fontSize: 10,
    fontWeight: "bold",
    color: COLORS.black,
    textAlign: "center",
    marginBottom: 3,
  },

  schoolProfileLocation: {
    fontSize: 7,
    color: COLORS.gray,
    textAlign: "center",
    lineHeight: 1.3,
  },

  /* ========================================================================
     STUDENT PROFILE
  ======================================================================== */

  studentProfileRow: {
    flexDirection: "row",
    alignItems: "center",
  },

  avatar: {
    width: 62,
    height: 62,
    borderRadius: 31,
    objectFit: "cover",
    marginRight: 10,
    borderWidth: 2,
    borderColor: COLORS.chameleon,
  },

  studentProfileDetails: {
    flex: 1,
  },

  profileStudentName: {
    fontSize: 11,
    fontWeight: "bold",
    color: COLORS.black,
    marginBottom: 4,
  },

  profileDetailRow: {
    flexDirection: "row",
    marginBottom: 3,
  },

  profileDetailLabel: {
    width: 55,
    fontSize: 7,
    color: COLORS.gray,
  },

  profileDetailValue: {
    flex: 1,
    fontSize: 7,
    fontWeight: "bold",
    color: COLORS.darkGray,
  },

  /* ========================================================================
     AVERAGE SUMMARY CIRCLES
  ======================================================================== */

  averageSummaryRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },

  averageSummaryItem: {
    width: 78,
    alignItems: "center",
  },

  averageSummaryCircle: {
    width: 58,
    height: 58,
    borderRadius: 29,
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 2,
    borderColor: COLORS.blueBorder,
    backgroundColor: COLORS.lightBlue,
  },

  averageSummaryCircleOrange: {
    width: 58,
    height: 58,
    borderRadius: 29,
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 2,
    borderColor: COLORS.deepOrange,
    backgroundColor: COLORS.lightOrange,
  },

  averageSummaryCircleGreen: {
    width: 58,
    height: 58,
    borderRadius: 29,
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 2,
    borderColor: COLORS.chameleon,
    backgroundColor: COLORS.lightChameleon,
  },

  averageSummaryLabel: {
    fontSize: 6,
    textAlign: "center",
    color: COLORS.gray,
    marginTop: 3,
  },

  averageSummaryValue: {
    fontSize: 10,
    fontWeight: "bold",
  },

  /* ========================================================================
     PROMOTION SECTION
  ======================================================================== */

  promotionOption: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 4,
    borderBottomWidth: 0.5,
    borderBottomColor: COLORS.borderGray,
  },

  promotionLetter: {
    width: 18,
    height: 18,
    borderRadius: 9,
    backgroundColor: COLORS.lightOrange,
    borderWidth: 1,
    borderColor: COLORS.deepOrange,
    justifyContent: "center",
    alignItems: "center",
    marginRight: 6,
  },

  promotionLetterText: {
    fontSize: 7,
    fontWeight: "bold",
    color: COLORS.deepOrange,
  },

  promotionOptionText: {
    flex: 1,
    fontSize: 7,
    color: COLORS.darkGray,
  },

  promotionLine: {
    width: 105,
    borderBottomWidth: 0.8,
    borderBottomColor: COLORS.black,
    marginLeft: 4,
  },

  /* ========================================================================
     PERIOD AVERAGES
  ======================================================================== */

  periodAverageRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 3,
    borderBottomWidth: 0.5,
    borderBottomColor: COLORS.borderGray,
  },

  periodAverageName: {
    fontSize: 7,
    color: COLORS.darkGray,
  },

  periodAverageValue: {
    fontSize: 7,
    fontWeight: "bold",
  },

  /* ========================================================================
     BACK FOOTER
  ======================================================================== */

  backFooter: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: COLORS.black,
    paddingVertical: 5,
    paddingHorizontal: 8,
    borderRadius: 4,
    marginTop: 2,
  },

  backFooterText: {
    color: COLORS.white,
    fontSize: 6,
  },

  backFooterHighlight: {
    color: COLORS.orange,
    fontSize: 6,
    fontWeight: "bold",
  },

  /* ========================================================================
     PARENT SIGNATURE
  ======================================================================== */

  parentSignatureCard: {
    backgroundColor: COLORS.lightChameleon,
    borderWidth: 1,
    borderColor: COLORS.chameleon,
    borderRadius: 6,
    padding: 8,
    alignItems: "center",
  },

  parentSignatureTitle: {
    fontSize: 8,
    fontWeight: "bold",
    color: COLORS.darkChameleon,
    marginBottom: 14,
  },

  parentSignatureLine: {
    width: "75%",
    borderTopWidth: 1,
    borderTopColor: COLORS.black,
    marginBottom: 3,
  },

  parentSignatureCaption: {
    fontSize: 6,
    color: COLORS.gray,
  },

  /* ========================================================================
     INFORMATION GRID
  ======================================================================== */

  infoGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
  },

  infoGridItem: {
    width: "50%",
    paddingVertical: 3,
  },

  infoGridLabel: {
    fontSize: 6,
    color: COLORS.gray,
  },

  infoGridValue: {
    fontSize: 7,
    fontWeight: "bold",
    color: COLORS.darkGray,
  },

});

/* ============================================================================
   REPORT CARD COMPONENT
============================================================================ */

const ReportCardPDF = ({
  responses = [],
  selectedGradeClass,
  selectedYear,
  showRemarksInPDF,
}) => {

  return (

    <Document>

      {responses.map((res, idx) => {

        if (!res?.data?.period_grades) {
          return null;
        }

        const data = res.data;

        /* ====================================================================
           DIVISION DETECTION
        ==================================================================== */

        const gradeClassName =
          data.grade_class_name || "";

        const division =
          gradeClassName.toLowerCase().includes("nursery") ||
          gradeClassName.toLowerCase().includes("kindergarten")
            ? "ECE"
            : "Elementary";

        /* ====================================================================
           SUBJECT ORDER
        ==================================================================== */

        const order =
          SUBJECT_ORDER[division] || [];

        const dataSubjects = [
          ...new Set(
            data.period_grades
              .map(
                (g) =>
                  g.subject_name?.trim()
              )
              .filter(Boolean)
          ),
        ];

        const extraSubjects =
          dataSubjects.filter(
            (subject) =>
              !order.some(
                (orderedSubject) =>
                  orderedSubject.toLowerCase() ===
                  subject.toLowerCase()
              )
          );

        const subjects = [
          ...order.filter(
            (subject) =>
              dataSubjects.some(
                (dataSubject) =>
                  dataSubject.toLowerCase() ===
                  subject.toLowerCase()
              )
          ),
          ...extraSubjects,
        ];

        /* ====================================================================
           GET SUBJECT AVERAGE
        ==================================================================== */

        const getSubjectAverage = (
          subject,
          field
        ) => {

          return (
            data.subjects_semester_averages?.find(
              (item) =>
                item.subject_name?.toLowerCase() ===
                subject.toLowerCase()
            )?.[field] ?? "N/A"
          );

        };

        /* ====================================================================
           GET SUBJECT GRADES
        ==================================================================== */

        const getSubjectGrades = (
          subject
        ) => {

          return data.period_grades.filter(
            (grade) =>
              grade.subject_name?.toLowerCase() ===
              subject.toLowerCase()
          );

        };

        /* ====================================================================
           PERIODS FOR BACK PAGE
        ==================================================================== */

        const allPeriods = [
          ...sem1Periods,
          ...sem2Periods,
        ];

        return (

          <React.Fragment key={idx}>

            {/* =============================================================
                FRONT PAGE
            ============================================================= */}

            <Page
              size="A4"
              orientation="landscape"
              style={styles.page}
              wrap={false}
            >

              {/* =========================================================
                  TOP ACCENT
              ========================================================= */}

              <View
                style={styles.topAccentBar}
              />

              {/* =========================================================
                  SCHOOL HEADER
              ========================================================= */}

              <View
                style={styles.header}
              >

                <View
                  style={styles.logoSection}
                >

                  <Image
                    src={logo}
                    style={styles.logo}
                  />

                  <View
                    style={styles.schoolIdentity}
                  >

                    <Text
                      style={styles.schoolName}
                    >
                      Jana L. Williams Educational Foundation 
                    
                    </Text>

                    <Text
                      style={styles.schoolSubtitle}
                    >
                      Academic Excellence • Character • Faith • Discipline
                    </Text>

                    <Text
                      style={styles.schoolLocation}
                    >
                     New Store Community, Mt. Barclay, Montserrado County, Liberia
                    </Text>

                  </View>

                </View>

                <View>

                  <Text
                    style={styles.reportCardTitle}
                  >
                    STUDENT REPORT CARD
                  </Text>

                  <Text
                    style={styles.reportCardSubtitle}
                  >
                    Official Academic Performance Record
                  </Text>

                </View>

<Image
    src={
        data.student_admission_image
            ? data.student_admission_image
            : studentAvatar
    }
    style={styles.avatar}
/>

              </View>

              {/* =========================================================
                  STUDENT INFORMATION
              ========================================================= */}

              <View
                style={styles.studentInfoBar}
              >

                <View
                  style={styles.studentInfoItem}
                >

                  <Text
                    style={styles.studentInfoLabel}
                  >
                    Student
                  </Text>

                  <Text
                    style={styles.studentInfoValue}
                  >
                    {data.student_name || "N/A"}
                  </Text>

                </View>

                <View
                  style={styles.studentInfoItem}
                >

                  <Text
                    style={styles.studentInfoLabel}
                  >
                    Class
                  </Text>

                  <Text
                    style={styles.studentInfoValue}
                  >
                    {data.grade_class_name || "N/A"}
                  </Text>

                </View>

                <View
                  style={styles.studentInfoItem}
                >

                  <Text
                    style={styles.studentInfoLabel}
                  >
                    Academic Year
                  </Text>

                  <Text
                    style={styles.studentInfoValue}
                  >
                    {selectedYear?.name || "N/A"}
                  </Text>

                </View>

                <View
                  style={styles.studentInfoItem}
                >

                  <Text
                    style={styles.studentInfoLabel}
                  >
                    Division
                  </Text>

                  <Text
                    style={styles.studentInfoValue}
                  >
                    {division}
                  </Text>

                </View>

              </View>

              {/* =========================================================
                  ACADEMIC PERFORMANCE SECTION
              ========================================================= */}

              <View
                style={styles.sectionHeader}
              >

                <Text
                  style={styles.sectionTitle}
                >
                  ACADEMIC PERFORMANCE SUMMARY
                </Text>

                <Text
                  style={styles.sectionTag}
                >
                  Annual Assessment Record
                </Text>

              </View>

              {/* =============================================================
                  EQUAL TWO-SIDE ACADEMIC TABLE
              ============================================================= */}

              <View
                style={styles.academicTableWrapper}
              >

                {/* =========================================================
                    LEFT HALF
                    SUBJECTS | P1 | P2 | P3 | EXAM | SA1
                ========================================================= */}

                <View
                  style={styles.academicHalfTable}
                >

                  {/* HEADER */}

                  <View
                    style={styles.academicHeaderRow}
                  >

                    <Text
                      style={
                        styles.academicSubjectHeaderCell
                      }
                    >
                      SUBJECTS
                    </Text>

                    {sem1Periods.map(
                      (period) => (

                        <Text
                          key={period}
                          style={
                            styles.academicScoreHeaderCell
                          }
                        >
                          {period}
                        </Text>

                      )
                    )}

                    <Text
                      style={[
                        styles.academicScoreHeaderCell,
                        {
                          backgroundColor:
                            COLORS.lightBlue,
                        },
                      ]}
                    >
                      SA1
                    </Text>

                  </View>

                  {/* SUBJECT ROWS */}

                  {subjects.map(
                    (subject) => {

                      const grades =
                        getSubjectGrades(
                          subject
                        );

                      const sem1Avg =
                        getSubjectAverage(
                          subject,
                          "semester_1_avg"
                        );

                      return (

                        <View
                          key={`sem1-${subject}`}
                          style={
                            styles.academicBodyRow
                          }
                        >

                          {/* SUBJECT */}

                          <Text
                            style={
                              styles.academicSubjectCell
                            }
                          >
                            {subject}
                          </Text>

                          {/* PERIODS 1–3 + EXAM */}

                          {sem1Periods.map(
                            (period) => {

                              const grade =
                                grades.find(
                                  (item) =>
                                    item.period_name ===
                                    period
                                );

                              const score =
                                formatValue(
                                  grade?.formatted_final_score
                                );

                              return (

                                <Text
                                  key={`${subject}-${period}`}
                                  style={[
                                    styles.academicScoreCell,
                                    {
                                      color:
                                        getColor(
                                          score
                                        ),
                                      backgroundColor:
                                        getScoreBackground(
                                          score
                                        ),
                                      fontSize:
                                        score === "N/A"
                                          ? 7.5
                                          : 9,
                                    },
                                  ]}
                                >
                                  {score}
                                </Text>

                              );

                            }
                          )}

                          {/* SA1 */}

                          <Text
                            style={[
                              styles.academicScoreCell,
                              styles.semesterAverageCell,
                              {
                                color:
                                  getColor(
                                    sem1Avg
                                  ),
                                fontWeight:
                                  "bold",
                              },
                            ]}
                          >
                            {formatValue(
                              sem1Avg
                            )}
                          </Text>

                        </View>

                      );

                    }
                  )}

                </View>

                {/* =========================================================
                    RIGHT HALF
                    P4 | P5 | P6 | EXAM2 | SA2 | YEARLY
                ========================================================= */}

                <View
                  style={[
                    styles.academicHalfTable,
                    styles.academicHalfTableRight,
                  ]}
                >

                  {/* HEADER */}

                  <View
                    style={styles.academicHeaderRow}
                  >

                    {sem2Periods.map(
                      (period) => (

                        <Text
                          key={period}
                          style={
                            styles.academicScoreHeaderCell
                          }
                        >
                          {period}
                        </Text>

                      )
                    )}

                    <Text
                      style={[
                        styles.academicScoreHeaderCell,
                        {
                          backgroundColor:
                            COLORS.lightBlue,
                        },
                      ]}
                    >
                      SA2
                    </Text>

                    <Text
                      style={[
                        styles.academicScoreHeaderCell,
                        {
                          backgroundColor:
                            COLORS.lightYellow,
                        },
                      ]}
                    >
                      YEARLY
                    </Text>

                  </View>

                  {/* SUBJECT ROWS */}

                  {subjects.map(
                    (subject) => {

                      const grades =
                        getSubjectGrades(
                          subject
                        );

                      const sem2Avg =
                        getSubjectAverage(
                          subject,
                          "semester_2_avg"
                        );

                      const yearlyAvg =
                        getSubjectAverage(
                          subject,
                          "yearly_avg"
                        );

                      return (

                        <View
                          key={`sem2-${subject}`}
                          style={
                            styles.academicBodyRow
                          }
                        >

                          {/* PERIODS 4–6 + EXAM2 */}

                          {sem2Periods.map(
                            (period) => {

                              const grade =
                                grades.find(
                                  (item) =>
                                    item.period_name ===
                                    period
                                );

                              const score =
                                formatValue(
                                  grade?.formatted_final_score
                                );

                              return (

                                <Text
                                  key={`${subject}-${period}`}
                                  style={[
                                    styles.academicScoreCell,
                                    {
                                      color:
                                        getColor(
                                          score
                                        ),
                                      backgroundColor:
                                        getScoreBackground(
                                          score
                                        ),
                                      fontSize:
                                        score === "N/A"
                                          ? 6
                                          : 7,
                                    },
                                  ]}
                                >
                                  {score}
                                </Text>

                              );

                            }
                          )}

                          {/* SA2 */}

                          <Text
                            style={[
                              styles.academicScoreCell,
                              styles.semesterAverageCell,
                              {
                                color:
                                  getColor(
                                    sem2Avg
                                  ),
                                fontWeight:
                                  "bold",
                              },
                            ]}
                          >
                            {formatValue(
                              sem2Avg
                            )}
                          </Text>

                          {/* YEARLY */}

                          <Text
                            style={[
                              styles.academicScoreCell,
                              styles.yearlyAverageCell,
                              {
                                color:
                                  getColor(
                                    yearlyAvg
                                  ),
                                fontWeight:
                                  "bold",
                              },
                            ]}
                          >
                            {formatValue(
                              yearlyAvg
                            )}
                          </Text>

                        </View>

                      );

                    }
                  )}

                </View>

              </View>

              {/* =========================================================
                  PERIOD AVERAGES
              ========================================================= */}

              <View
                style={[
                  styles.academicTableWrapper,
                  {
                    marginTop: 0,
                  },
                ]}
              >

                {/* LEFT PERIOD AVERAGES */}

                <View
                  style={styles.academicHalfTable}
                >

                  <View
                    style={[
                      styles.academicBodyRow,
                      {
                        backgroundColor:
                          COLORS.lightGray,
                      },
                    ]}
                  >

                    <Text
                      style={
                        styles.academicSubjectCell
                      }
                    >
                      PERIOD AVERAGES
                    </Text>

                    {sem1Periods.map(
                      (periodName) => {

                        const avg =
                          data.period_average?.[
                            periodName
                          ] ?? "N/A";

                        return (

                          <View
                            key={periodName}
                            style={
                              styles.academicScoreCell
                            }
                          >

                            <Text
                              style={[
                                styles.averageText,
                                {
                                  color:
                                    getColor(
                                      avg
                                    ),
                                },
                              ]}
                            >
                              {formatValue(
                                avg
                              )}
                            </Text>

                          </View>

                        );

                      }
                    )}

                    <View
                      style={
                        styles.academicScoreCell
                      }
                    >

                      <Text
                        style={
                          styles.averageLabel
                        }
                      >
                        SA1
                      </Text>

                      <Text
                        style={[
                          styles.averageText,
                          {
                            color:
                              getColor(
                                data.semester_1_avg
                              ),
                            fontWeight:
                              "bold",
                          },
                        ]}
                      >
                        {formatValue(
                          data.semester_1_avg
                        )}
                      </Text>

                    </View>

                  </View>

                </View>

                {/* RIGHT PERIOD AVERAGES */}

                <View
                  style={[
                    styles.academicHalfTable,
                    styles.academicHalfTableRight,
                  ]}
                >

                  <View
                    style={[
                      styles.academicBodyRow,
                      {
                        backgroundColor:
                          COLORS.lightGray,
                      },
                    ]}
                  >

                    {sem2Periods.map(
                      (periodName) => {

                        const avg =
                          data.period_average?.[
                            periodName
                          ] ?? "N/A";

                        return (

                          <View
                            key={periodName}
                            style={
                              styles.academicScoreCell
                            }
                          >

                            <Text
                              style={[
                                styles.averageText,
                                {
                                  color:
                                    getColor(
                                      avg
                                    ),
                                },
                              ]}
                            >
                              {formatValue(
                                avg
                              )}
                            </Text>

                          </View>

                        );

                      }
                    )}

                    <View
                      style={
                        styles.academicScoreCell
                      }
                    >

                      <Text
                        style={
                          styles.averageLabel
                        }
                      >
                        SA2
                      </Text>

                      <Text
                        style={[
                          styles.averageText,
                          {
                            color:
                              getColor(
                                data.semester_2_avg
                              ),
                            fontWeight:
                              "bold",
                          },
                        ]}
                      >
                        {formatValue(
                          data.semester_2_avg
                        )}
                      </Text>

                    </View>

                    <View
                      style={
                        styles.academicScoreCell
                      }
                    >

                      <Text
                        style={
                          styles.averageLabel
                        }
                      >
                        YEARLY
                      </Text>

                      <Text
                        style={[
                          styles.averageText,
                          {
                            color:
                              getColor(
                                data.yearly_average
                              ),
                            fontWeight:
                              "bold",
                          },
                        ]}
                      >
                        {formatValue(
                          data.yearly_average
                        )}
                      </Text>

                    </View>

                  </View>

                </View>

              </View>

              {/* =========================================================
                  SUMMARY CARDS
              ========================================================= */}

              <View
                style={
                  styles.performanceSummary
                }
              >

                <View
                  style={styles.summaryCard}
                >

                  <Text
                    style={
                      styles.summaryLabel
                    }
                  >
                    SEMESTER 1 AVERAGE
                  </Text>

                  <Text
                    style={[
                      styles.summaryValue,
                      {
                        color:
                          getColor(
                            data.semester_1_avg
                          ),
                      },
                    ]}
                  >
                    {formatValue(
                      data.semester_1_avg
                    )}
                  </Text>

                </View>

                <View
                  style={styles.summaryCard}
                >

                  <Text
                    style={
                      styles.summaryLabel
                    }
                  >
                    SEMESTER 2 AVERAGE
                  </Text>

                  <Text
                    style={[
                      styles.summaryValue,
                      {
                        color:
                          getColor(
                            data.semester_2_avg
                          ),
                      },
                    ]}
                  >
                    {formatValue(
                      data.semester_2_avg
                    )}
                  </Text>

                </View>

                <View
                  style={styles.summaryCardLast}
                >

                  <Text
                    style={
                      styles.summaryLabel
                    }
                  >
                    FINAL YEARLY AVERAGE
                  </Text>

                  <Text
                    style={[
                      styles.summaryValue,
                      {
                        color:
                          getColor(
                            data.yearly_average
                          ),
                      },
                    ]}
                  >
                    {formatValue(
                      data.yearly_average
                    )}
                  </Text>

                </View>

              </View>

              {/* =========================================================
                  PROMOTION POLICY
              ========================================================= */}

              <View
                style={
                  styles.promotionBox
                }
              >

                <Text
                  style={
                    styles.promotionTitle
                  }
                >
                  PROMOTION POLICY
                </Text>

                <Text
                  style={
                    styles.promotionText
                  }
                >
                  A student who fails two major subjects
                  may not be eligible for promotion. A
                  student who fails one major subject may
                  be required to attend vacation school or
                  complete an approved academic intervention
                  program.
                </Text>

              </View>

              {/* =========================================================
                  REMARK
              ========================================================= */}

              <View
                style={styles.remarkBox}
              >

                <Text
                  style={
                    styles.remarkLabel
                  }
                >
                  FINAL REMARK:
                </Text>

                <Text
                  style={
                    styles.remarkText
                  }
                >
                  {showRemarksInPDF
                    ? data.remark ||
                      "No remark provided."
                    : "Year-End Remark will appear here after review."}
                </Text>

              </View>

              {/* =========================================================
                  GRADE LEGEND
              ========================================================= */}

              <Text
                style={
                  styles.gradeLegend
                }
              >
                GRADING SCALE:
                {"  "}
                60–69 FAIL
                {"  |  "}
                70–79 FAIR
                {"  |  "}
                80–89 GOOD
                {"  |  "}
                90–95 VERY GOOD
                {"  |  "}
                96–100 EXCELLENT
              </Text>

              {/* =========================================================
                  CONDUCT AND RANK
              ========================================================= */}

              <View
                style={
                  styles.conductRankBar
                }
              >

                <Text
                  style={
                    styles.conductText
                  }
                >
                  YEARLY CONDUCT:
                  {" "}
                  {data.yearly_conduct ||
                    "N/A"}
                </Text>

                <View
                  style={
                    styles.rankBadge
                  }
                >

                  <Text
                    style={
                      styles.rankText
                    }
                  >
                    CLASS RANK:
                    {" "}
                    {data.rank ||
                      "N/A"}
                  </Text>

                </View>

              </View>

              {/* =========================================================
                  SIGNATURES
              ========================================================= */}

              <View
                style={
                  styles.signatureSection
                }
              >

                <View
                  style={
                    styles.signatureItem
                  }
                >

                  <Text
                    style={
                      styles.signatureLabel
                    }
                  >
                    PRINCIPAL'S SIGNATURE
                  </Text>

                  <View
                    style={
                      styles.signatureLine
                    }
                  />

                  <Text
                    style={
                      styles.signatureDate
                    }
                  >
                    Date: ____________________
                  </Text>

                </View>

                <View
                  style={
                    styles.signatureItem
                  }
                >

                  <Text
                    style={
                      styles.signatureLabel
                    }
                  >
                    CLASS SPONSOR'S SIGNATURE
                  </Text>

                  <View
                    style={
                      styles.signatureLine
                    }
                  />

                  <Text
                    style={
                      styles.signatureDate
                    }
                  >
                    Date: ____________________
                  </Text>

                </View>

              </View>

            </Page>

            {/* ===============================================================
                BACK PAGE
            =============================================================== */}

            <Page
              size="A4"
              orientation="landscape"
              style={styles.backPage}
              wrap={false}
            >

              {/* =========================================================
                  BACK PAGE HEADER
              ========================================================= */}

              <View
                style={
                  styles.backPageHeader
                }
              >

                <View
                  style={
                    styles.backPageHeaderLeft
                  }
                >

                  <Image
                    src={logo}
                    style={
                      styles.backPageLogo
                    }
                  />

                  <View>

                    <Text
                      style={
                        styles.backPageTitle
                      }
                    >
                      STUDENT ACADEMIC PROFILE
                    </Text>

                    <Text
                      style={
                        styles.backPageSubtitle
                      }
                    >
                      Official school record • Student development • Promotion
                    </Text>

                  </View>

                </View>

                <Text
                  style={
                    styles.backPageTag
                  }
                >
                  BACK PAGE
                </Text>

              </View>

              {/* =========================================================
                  TWO EQUAL BACK COLUMNS
              ========================================================= */}

              <View
                style={
                  styles.backColumns
                }
              >

                {/* =======================================================
                    LEFT COLUMN
                ======================================================= */}

                <View
                  style={
                    styles.backColumnLeft
                  }
                >

                  {/* SCHOOL INFORMATION */}

                  <View
                    style={
                      styles.card
                    }
                  >

                    <View
                      style={
                        styles.cardHeader
                      }
                    >

                      <View
                        style={
                          styles.cardHeaderAccent
                        }
                      />

                      <Text
                        style={
                          styles.cardTitle
                        }
                      >
                        SCHOOL INFORMATION
                      </Text>

                    </View>

                    <View
                      style={
                        styles.schoolProfileContent
                      }
                    >

                      <Image
                        src={logo}
                        style={
                          styles.schoolProfileLogo
                        }
                      />

                      <Text
                        style={
                          styles.schoolProfileName
                        }
                      >
                        Jana L. Williams Educational Foundation School System
                      
                      </Text>

                      <Text
                        style={
                          styles.schoolProfileLocation
                        }
                      >
                        Zazay Community, Paynesville City,
                        Monrovia, Liberia
                        {/* Mt. Barclay New Store Community, Monrovia Liberia */}
                        
                      </Text>

                      <View
                        style={
                          styles.infoGrid
                        }
                      >

                        <View
                          style={
                            styles.infoGridItem
                          }
                        >

                          <Text
                            style={
                              styles.infoGridLabel
                            }
                          >
                            SCHOOL YEAR
                          </Text>

                          <Text
                            style={
                              styles.infoGridValue
                            }
                          >
                            {selectedYear?.name ||
                              "N/A"}
                          </Text>

                        </View>

                        <View
                          style={
                            styles.infoGridItem
                          }
                        >

                          <Text
                            style={
                              styles.infoGridLabel
                            }
                          >
                            DIVISION
                          </Text>

                          <Text
                            style={
                              styles.infoGridValue
                            }
                          >
                            {division}
                          </Text>

                        </View>

                      </View>

                    </View>

                  </View>

                  {/* STUDENT PROFILE */}

                  <View
                    style={
                      styles.card
                    }
                  >

                    <View
                      style={
                        styles.cardHeader
                      }
                    >

                      <View
                        style={
                          styles.cardHeaderAccent
                        }
                      />

                      <Text
                        style={
                          styles.cardTitle
                        }
                      >
                        STUDENT PROFILE
                      </Text>

                    </View>

                    <View
                      style={
                        styles.studentProfileRow
                      }
                    >

<Image
    src={
        data.student_admission_image
            ? data.student_admission_image
            : studentAvatar
    }
    style={styles.avatar}
/>

                      <View
                        style={
                          styles.studentProfileDetails
                        }
                      >

                        <Text
                          style={
                            styles.profileStudentName
                          }
                        >
                          {data.student_name ||
                            "N/A"}
                        </Text>

                        <View
                          style={
                            styles.profileDetailRow
                          }
                        >

                          <Text
                            style={
                              styles.profileDetailLabel
                            }
                          >
                            Class:
                          </Text>

                          <Text
                            style={
                              styles.profileDetailValue
                            }
                          >
                            {data.grade_class_name ||
                              "N/A"}
                          </Text>

                        </View>

                        <View
                          style={
                            styles.profileDetailRow
                          }
                        >

                          <Text
                            style={
                              styles.profileDetailLabel
                            }
                          >
                            Academic Year:
                          </Text>

                          <Text
                            style={
                              styles.profileDetailValue
                            }
                          >
                            {selectedYear?.name ||
                              "N/A"}
                          </Text>

                        </View>

                        <View
                          style={
                            styles.profileDetailRow
                          }
                        >

                          <Text
                            style={
                              styles.profileDetailLabel
                            }
                          >
                            Rank:
                          </Text>

                          <Text
                            style={
                              styles.profileDetailValue
                            }
                          >
                            {data.rank ||
                              "N/A"}
                          </Text>

                        </View>

                      </View>

                    </View>

                  </View>

                  {/* PERFORMANCE OVERVIEW */}

                  <View
                    style={
                      styles.card
                    }
                  >

                    <View
                      style={
                        styles.cardHeader
                      }
                    >

                      <View
                        style={
                          styles.cardHeaderAccent
                        }
                      />

                      <Text
                        style={
                          styles.cardTitle
                        }
                      >
                        PERFORMANCE OVERVIEW
                      </Text>

                    </View>

                    <View
                      style={
                        styles.averageSummaryRow
                      }
                    >

                      <View
                        style={
                          styles.averageSummaryItem
                        }
                      >

                        <View
                          style={
                            styles.averageSummaryCircle
                          }
                        >

                          <Text
                            style={[
                              styles.averageSummaryValue,
                              {
                                color:
                                  getColor(
                                    data.semester_1_avg
                                  ),
                              },
                            ]}
                          >
                            {formatValue(
                              data.semester_1_avg
                            )}
                          </Text>

                        </View>

                        <Text
                          style={
                            styles.averageSummaryLabel
                          }
                        >
                          SEMESTER 1
                        </Text>

                      </View>

                      <View
                        style={
                          styles.averageSummaryItem
                        }
                      >

                        <View
                          style={
                            styles.averageSummaryCircleOrange
                          }
                        >

                          <Text
                            style={[
                              styles.averageSummaryValue,
                              {
                                color:
                                  getColor(
                                    data.semester_2_avg
                                  ),
                              },
                            ]}
                          >
                            {formatValue(
                              data.semester_2_avg
                            )}
                          </Text>

                        </View>

                        <Text
                          style={
                            styles.averageSummaryLabel
                          }
                        >
                          SEMESTER 2
                        </Text>

                      </View>

                      <View
                        style={
                          styles.averageSummaryItem
                        }
                      >

                        <View
                          style={
                            styles.averageSummaryCircleGreen
                          }
                        >

                          <Text
                            style={[
                              styles.averageSummaryValue,
                              {
                                color:
                                  getColor(
                                    data.yearly_average
                                  ),
                              },
                            ]}
                          >
                            {formatValue(
                              data.yearly_average
                            )}
                          </Text>

                        </View>

                        <Text
                          style={
                            styles.averageSummaryLabel
                          }
                        >
                          FINAL YEARLY
                        </Text>

                      </View>

                    </View>

                  </View>

                  {/* STUDENT DEVELOPMENT */}

                  <View
                    style={
                      styles.card
                    }
                  >

                    <View
                      style={
                        styles.cardHeader
                      }
                    >

                      <View
                        style={
                          styles.cardHeaderAccent
                        }
                      />

                      <Text
                        style={
                          styles.cardTitle
                        }
                      >
                        STUDENT DEVELOPMENT
                      </Text>

                    </View>

                    <View
                      style={
                        styles.infoGrid
                      }
                    >

                      <View
                        style={
                          styles.infoGridItem
                        }
                      >

                        <Text
                          style={
                            styles.infoGridLabel
                          }
                        >
                          YEARLY CONDUCT
                        </Text>

                        <Text
                          style={
                            styles.infoGridValue
                          }
                        >
                          {data.yearly_conduct ||
                            "N/A"}
                        </Text>

                      </View>

                      <View
                        style={
                          styles.infoGridItem
                        }
                      >

                        <Text
                          style={
                            styles.infoGridLabel
                          }
                        >
                          CLASS RANK
                        </Text>

                        <Text
                          style={
                            styles.infoGridValue
                          }
                        >
                          {data.rank ||
                            "N/A"}
                        </Text>

                      </View>

                    </View>

                  </View>

                </View>

                {/* =========================================================
                    RIGHT COLUMN
                ========================================================= */}

                <View
                  style={
                    styles.backColumnRight
                  }
                >

                  {/* PROMOTION DECISION */}

                  <View
                    style={
                      styles.card
                    }
                  >

                    <View
                      style={
                        styles.cardHeader
                      }
                    >

                      <View
                        style={
                          styles.cardHeaderAccent
                        }
                      />

                      <Text
                        style={
                          styles.cardTitle
                        }
                      >
                        PROMOTION DECISION
                      </Text>

                    </View>

                    <View
                      style={
                        styles.promotionOption
                      }
                    >

                      <View
                        style={
                          styles.promotionLetter
                        }
                      >

                        <Text
                          style={
                            styles.promotionLetterText
                          }
                        >
                          A
                        </Text>

                      </View>

                      <Text
                        style={
                          styles.promotionOptionText
                        }
                      >
                        Promoted to:
                      </Text>

                      <View
                        style={
                          styles.promotionLine
                        }
                      />

                    </View>

                    <View
                      style={
                        styles.promotionOption
                      }
                    >

                      <View
                        style={
                          styles.promotionLetter
                        }
                      >

                        <Text
                          style={
                            styles.promotionLetterText
                          }
                        >
                          B
                        </Text>

                      </View>

                      <Text
                        style={
                          styles.promotionOptionText
                        }
                      >
                        Condition in:
                      </Text>

                      <View
                        style={
                          styles.promotionLine
                        }
                      />

                    </View>

                    <View
                      style={
                        styles.promotionOption
                      }
                    >

                      <View
                        style={
                          styles.promotionLetter
                        }
                      >

                        <Text
                          style={
                            styles.promotionLetterText
                          }
                        >
                          C
                        </Text>

                      </View>

                      <Text
                        style={
                          styles.promotionOptionText
                        }
                      >
                        Fail / Repeat Grade Class
                      </Text>

                    </View>

                    <View
                      style={
                        styles.promotionOption
                      }
                    >

                      <View
                        style={
                          styles.promotionLetter
                        }
                      >

                        <Text
                          style={
                            styles.promotionLetterText
                          }
                        >
                          D
                        </Text>

                      </View>

                      <Text
                        style={
                          styles.promotionOptionText
                        }
                      >
                        Never to Return
                      </Text>

                    </View>

                  </View>

                  {/* PERIOD AVERAGES */}

                  <View
                    style={
                      styles.card
                    }
                  >

                    <View
                      style={
                        styles.cardHeader
                      }
                    >

                      <View
                        style={
                          styles.cardHeaderAccent
                        }
                      />

                      <Text
                        style={
                          styles.cardTitle
                        }
                      >
                        PERIOD AVERAGES
                      </Text>

                    </View>

                    {allPeriods.map(
                      (period) => (

                        <View
                          key={period}
                          style={
                            styles.periodAverageRow
                          }
                        >

                          <Text
                            style={
                              styles.periodAverageName
                            }
                          >
                            {period}
                          </Text>

                          <Text
                            style={[
                              styles.periodAverageValue,
                              {
                                color:
                                  getColor(
                                    data.period_average?.[
                                      period
                                    ]
                                  ),
                              },
                            ]}
                          >
                            {formatValue(
                              data.period_average?.[
                                period
                              ]
                            )}
                          </Text>

                        </View>

                      )
                    )}

                  </View>

                  {/* FINAL ACADEMIC SUMMARY */}

                  <View
                    style={
                      styles.card
                    }
                  >

                    <View
                      style={
                        styles.cardHeader
                      }
                    >

                      <View
                        style={
                          styles.cardHeaderAccent
                        }
                      />

                      <Text
                        style={
                          styles.cardTitle
                        }
                      >
                        FINAL ACADEMIC SUMMARY
                      </Text>

                    </View>

                    <View
                      style={
                        styles.infoGrid
                      }
                    >

                      <View
                        style={
                          styles.infoGridItem
                        }
                      >

                        <Text
                          style={
                            styles.infoGridLabel
                          }
                        >
                          SEMESTER 1 AVERAGE
                        </Text>

                        <Text
                          style={[
                            styles.infoGridValue,
                            {
                              color:
                                getColor(
                                  data.semester_1_avg
                                ),
                            },
                          ]}
                        >
                          {formatValue(
                            data.semester_1_avg
                          )}
                        </Text>

                      </View>

                      <View
                        style={
                          styles.infoGridItem
                        }
                      >

                        <Text
                          style={
                            styles.infoGridLabel
                          }
                        >
                          SEMESTER 2 AVERAGE
                        </Text>

                        <Text
                          style={[
                            styles.infoGridValue,
                            {
                              color:
                                getColor(
                                  data.semester_2_avg
                                ),
                            },
                          ]}
                        >
                          {formatValue(
                            data.semester_2_avg
                          )}
                        </Text>

                      </View>

                      <View
                        style={
                          styles.infoGridItem
                        }
                      >

                        <Text
                          style={
                            styles.infoGridLabel
                          }
                        >
                          YEARLY AVERAGE
                        </Text>

                        <Text
                          style={[
                            styles.infoGridValue,
                            {
                              color:
                                getColor(
                                  data.yearly_average
                                ),
                            },
                          ]}
                        >
                          {formatValue(
                            data.yearly_average
                          )}
                        </Text>

                      </View>

                      <View
                        style={
                          styles.infoGridItem
                        }
                      >

                        <Text
                          style={
                            styles.infoGridLabel
                          }
                        >
                          CLASS RANK
                        </Text>

                        <Text
                          style={
                            styles.infoGridValue
                          }
                        >
                          {data.rank ||
                            "N/A"}
                        </Text>

                      </View>

                    </View>

                  </View>

                  {/* PARENT SIGNATURE */}

                  <View
                    style={
                      styles.parentSignatureCard
                    }
                  >

                    <Text
                      style={
                        styles.parentSignatureTitle
                      }
                    >
                      PARENT / GUARDIAN SIGNATURE
                    </Text>

                    <View
                      style={
                        styles.parentSignatureLine
                      }
                    />

                    <Text
                      style={
                        styles.parentSignatureCaption
                      }
                    >
                      Signature and Date
                    </Text>

                  </View>

                </View>

              </View>

              {/* =========================================================
                  BACK FOOTER
              ========================================================= */}

              <View
                style={
                  styles.backFooter
                }
              >

                <Text
                  style={
                    styles.backFooterText
                  }
                >
                  This report card is an official academic
                  record of the school. -- Design By: Polu Intergrated Technology - School Management System
                </Text>

                <Text
                  style={
                    styles.backFooterHighlight
                  }
                >
                  EDUCATION • CHARACTER • EXCELLENCE
                </Text>

              </View>

            </Page>

          </React.Fragment>

        );

      })}

    </Document>

  );

};

export default ReportCardPDF;

